import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
  memo
} from 'react';

// Material UI imports
import { Box, makeStyles } from '@material-ui/core';

// Skeleton component
import { Skeleton } from '@material-ui/lab';

// Snackbar provider
import { useSnackbar } from 'notistack';

// Authentication context
import { AuthContext } from 'services/auth';

// AsyncStatus
import { AsyncStatus } from 'components';
import { pdfjs, Document, Page } from 'react-pdf';
// Style
import styles from './styles';
import useWindowSize from 'hooks/useWindowSize';
import { PDFLinkService } from 'pdfjs-dist/web/pdf_viewer';

// PDF.js configuration
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const useStyles = makeStyles(styles);

// Enhanced global CSS for completely responsive PDF with annotation support
const pdfResponsiveStyles = `
  .materials-pdf-container {
    width: 100%;
    height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 10px;
    background-color: #f5f5f5;
    box-sizing: border-box;
  }
  
  .materials-pdf-container .react-pdf__Document {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .materials-pdf-container .react-pdf__Page {
    width: 100% !important;
    max-width: 100% !important;
    margin-bottom: 10px !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-radius: 4px;
    background-color: white;
    overflow: hidden;
    position: relative;
  }
  
  .materials-pdf-container .react-pdf__Page__canvas {
    width: 100% !important;
    height: auto !important;
    max-width: 100% !important;
    display: block;
  }

  /* Annotation layer styles for clickable links */
  .materials-pdf-container .react-pdf__Page__annotations {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    pointer-events: none;
  }

  .materials-pdf-container .react-pdf__Page__annotations .linkAnnotation,
  .materials-pdf-container .react-pdf__Page__annotations .linkAnnotation > a {
    pointer-events: auto !important;
    cursor: pointer !important;
    display: block;
    text-decoration: none;
  }

  .materials-pdf-container .react-pdf__Page__annotations .linkAnnotation:hover,
  .materials-pdf-container .react-pdf__Page__annotations .linkAnnotation > a:hover {
    background-color: rgba(0, 123, 255, 0.1) !important;
    border-radius: 2px;
  }

  /* Ensure all annotation elements are clickable */
  .materials-pdf-container .react-pdf__Page__annotations section {
    pointer-events: auto !important;
  }

  .materials-pdf-page-wrapper {
    width: 100%;
    margin-bottom: 10px;
    display: flex;
    justify-content: center;
    position: relative;
  }
`;

// Inject styles into head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = pdfResponsiveStyles;
  if (!document.head.querySelector('style[data-materials-pdf]')) {
    styleSheet.setAttribute('data-materials-pdf', 'true');
    document.head.appendChild(styleSheet);
  }
}

// Enhanced Error Boundary with better zoom handling
class PDFErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    };
    this.retryTimeoutId = null;
    this.maxRetries = 3;
  }

  static getDerivedStateFromError(error) {
    console.log('PDF Error caught:', error.message);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PDF rendering error:', error, errorInfo);
    this.setState({ errorInfo });

    // Auto-retry for common zoom/resize errors
    if (
      this.shouldAutoRetry(error) &&
      this.state.retryCount < this.maxRetries
    ) {
      this.setState({ isRetrying: true });

      // Clear any existing timeout
      if (this.retryTimeoutId) {
        clearTimeout(this.retryTimeoutId);
      }

      // Retry after a short delay
      this.retryTimeoutId = setTimeout(() => {
        this.setState((prevState) => ({
          hasError: false,
          errorInfo: null,
          retryCount: prevState.retryCount + 1,
          isRetrying: false
        }));

        // Trigger parent component re-render if callback provided
        if (this.props.onAutoRetry) {
          this.props.onAutoRetry();
        }
      }, 1000);
    }
  }

  shouldAutoRetry(error) {
    const errorMessage = error.message?.toLowerCase() || '';
    const zoomRelatedErrors = [
      'width',
      'height',
      'canvas',
      'render',
      'resize',
      'scale',
      'viewport'
    ];

    return zoomRelatedErrors.some((keyword) => errorMessage.includes(keyword));
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleManualRetry = () => {
    this.setState({
      hasError: false,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    });

    if (this.props.onManualRetry) {
      this.props.onManualRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isRetrying) {
        return (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}>
            <div>Adjusting to zoom level...</div>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #2196f3',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginTop: '10px'
              }}></div>
            <style jsx>{`
              @keyframes spin {
                0% {
                  transform: rotate(0deg);
                }
                100% {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </div>
        );
      }

      // Only show error UI if we've exhausted retries
      if (this.state.retryCount >= this.maxRetries) {
        return (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: '#f44336',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}>
            <div>Unable to display PDF at current zoom level</div>
            <div
              style={{
                fontSize: '14px',
                color: '#666',
                marginTop: '5px',
                marginBottom: '15px'
              }}>
              Try adjusting your browser zoom or refresh the page
            </div>
            <button
              onClick={this.handleManualRetry}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#2196f3',
                color: 'white',
                cursor: 'pointer'
              }}>
              Retry
            </button>
          </div>
        );
      }
    }

    return this.props.children;
  }
}

// Enhanced PDF Viewer component with zoom resilience and clickable links
const EnhancedPdfViewer = ({ url }) => {
  const [numPages, setNumPages] = useState();
  const [loadError, setLoadError] = useState(null);
  const [documentKey, setDocumentKey] = useState(0);
  const [isZoomChanging, setIsZoomChanging] = useState(false);
  const [originalAnnotations, setOriginalAnnotations] = useState(new Map());
  const documentRef = useRef();
  const zoomTimeoutRef = useRef();
  const linkServiceRef = useRef(new PDFLinkService());

  // Memoize options to prevent unnecessary reloads
  const options = useMemo(
    () => ({
      cMapUrl: '/cmaps/',
      standardFontDataUrl: '/standard_fonts/',
      disableAutoFetch: false,
      disableStream: false,
      disableRange: false
    }),
    []
  );

  // Handle zoom changes
  useEffect(() => {
    const handleZoomChange = () => {
      setIsZoomChanging(true);

      // Clear existing timeout
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }

      // Debounce zoom changes
      zoomTimeoutRef.current = setTimeout(() => {
        setIsZoomChanging(false);
        // Force re-render of PDF document
        setDocumentKey((prev) => prev + 1);
      }, 300);
    };

    // Listen for zoom/resize events
    window.addEventListener('resize', handleZoomChange);

    // Listen for zoom events (wheel + ctrl/cmd)
    const handleWheelZoom = (e) => {
      if (e.ctrlKey || e.metaKey) {
        handleZoomChange();
      }
    };

    window.addEventListener('wheel', handleWheelZoom, { passive: true });

    // Listen for keyboard zoom (Ctrl/Cmd + +/-)
    const handleKeyboardZoom = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === '+' || e.key === '-' || e.key === '0')
      ) {
        handleZoomChange();
      }
    };

    window.addEventListener('keydown', handleKeyboardZoom);

    return () => {
      window.removeEventListener('resize', handleZoomChange);
      window.removeEventListener('wheel', handleWheelZoom);
      window.removeEventListener('keydown', handleKeyboardZoom);
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setLoadError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setLoadError('Failed to load PDF. Please try again.');
  };

  const handleAutoRetry = useCallback(() => {
    setDocumentKey((prev) => prev + 1);
  }, []);

  const handleManualRetry = useCallback(() => {
    setLoadError(null);
    setDocumentKey((prev) => prev + 1);
  }, []);

  // Handle link clicks in PDF
  const onItemClick = useCallback(({ pageNumber, dest }) => {
    // Handle internal PDF links (bookmarks, table of contents, etc.)
    console.log('PDF internal link clicked:', { pageNumber, dest });
    // You can add custom logic here for internal navigation
  }, []);

  // Handle external link clicks
  const onLinkClick = useCallback((url) => {
    console.log(url);
    // Open external links in new tab
    // window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  // Handle all link clicks from PDF annotations
  const handleLinkClick = useCallback(
    (event) => {
      // Check if the clicked element is a link or inside a link
      const link = event.target.closest('a') || event.target;

      // Try to get the page number to look up original annotations
      const pageWrapper = event.target.closest('.materials-pdf-page-wrapper');
      const pageElement = pageWrapper?.querySelector('[data-page-number]');
      const pageNumber = pageElement?.getAttribute('data-page-number');

      // Check various ways a link might be represented in PDF annotations
      let linkUrl = null;
      let originalUrl = null;

      // Helper function to decode potential ROT13 or other encoded URLs
      const decodeUrl = (encodedUrl) => {
        if (!encodedUrl) return null;

        // Try ROT13 decoding
        const rot13 = (str) => {
          return str.replace(/[a-zA-Z]/g, (char) => {
            const start = char <= 'Z' ? 65 : 97;
            return String.fromCharCode(
              ((char.charCodeAt(0) - start + 13) % 26) + start
            );
          });
        };

        let decoded = rot13(encodedUrl);

        // Check if the decoded string looks like a URL
        if (
          decoded.includes('http') ||
          decoded.includes('www.') ||
          decoded.includes('.com') ||
          decoded.includes('.org')
        ) {
          // Clean up the decoded URL
          if (
            !decoded.startsWith('http://') &&
            !decoded.startsWith('https://')
          ) {
            if (decoded.startsWith('www.')) {
              decoded = 'https://' + decoded;
            } else if (decoded.includes('.')) {
              decoded = 'https://' + decoded;
            }
          }
          return decoded;
        }

        // Try other decoding methods if needed
        // For now, return the original if ROT13 doesn't work
        return encodedUrl;
      };

      // First, try to get the original URL from our stored annotations
      if (pageNumber && originalAnnotations.has(parseInt(pageNumber))) {
        const pageAnnotations = originalAnnotations.get(parseInt(pageNumber));

        // Try to match this click to an original annotation
        const linkRect = link.getBoundingClientRect();
        const pageRect = pageWrapper.getBoundingClientRect();

        // Convert click position to relative page coordinates
        const relativeX = (linkRect.left - pageRect.left) / pageRect.width;
        const relativeY = (linkRect.top - pageRect.top) / pageRect.height;

        console.log('Click position:', {
          relativeX,
          relativeY,
          pageAnnotations
        });

        // Find the closest annotation based on position
        let bestMatch = null;
        let minDistance = Infinity;

        pageAnnotations.forEach((annotation) => {
          if (annotation.url && annotation.rect) {
            // PDF coordinates are from bottom-left, convert to top-left
            const [x1, y1, x2, y2] = annotation.rect;
            const centerX = (x1 + x2) / 2;
            const centerY = (y1 + y2) / 2;

            // Rough distance calculation (you might need to adjust this based on PDF coordinate system)
            const distance =
              Math.abs(relativeX - centerX / 600) +
              Math.abs(relativeY - centerY / 800);

            if (distance < minDistance) {
              minDistance = distance;
              bestMatch = annotation;
            }
          }
        });

        if (bestMatch) {
          const decodedUrl = decodeUrl(bestMatch.url);
          if (decodedUrl && !decodedUrl.includes('localhost')) {
            originalUrl = decodedUrl;
            console.log(
              'Found and decoded original URL from annotations:',
              originalUrl
            );
          }
        }

        // If no position match, try to decode all URLs and use the first valid one
        if (!originalUrl) {
          for (const annotation of pageAnnotations) {
            const decodedUrl = decodeUrl(annotation.url);
            if (
              decodedUrl &&
              (decodedUrl.startsWith('http://') ||
                decodedUrl.startsWith('https://') ||
                decodedUrl.includes('www.')) &&
              !decodedUrl.includes('localhost')
            ) {
              originalUrl = decodedUrl;
              console.log(
                'Found decoded URL from annotations (fallback):',
                originalUrl
              );
              break;
            }
          }
        }
      }

      if (link.href) {
        linkUrl = link.href;
      } else if (link.getAttribute && link.getAttribute('data-url')) {
        linkUrl = link.getAttribute('data-url');
      } else if (
        link.title &&
        (link.title.startsWith('http://') || link.title.startsWith('https://'))
      ) {
        linkUrl = link.title;
      }

      // Also check parent elements for link information
      if (!linkUrl && link.parentElement) {
        const parent = link.parentElement.closest('a');
        if (parent && parent.href) {
          linkUrl = parent.href;
        }
      }

      if (linkUrl || originalUrl) {
        // Always prevent default navigation
        event.preventDefault();
        event.stopPropagation();

        // Prefer original URL if we found one
        const urlToUse = originalUrl || linkUrl;

        // Debug logging
        console.log('PDF Link Debug:', {
          linkUrl,
          originalUrl,
          urlToUse,
          pageNumber,
          textContent: link.textContent?.trim(),
          title: link.title
        });

        // Check if this is a localhost URL from the current application
        if (
          urlToUse.includes('localhost:3000') ||
          urlToUse.includes(window.location.host)
        ) {
          console.log('Skipping localhost URL:', urlToUse);
          return false;
        }

        // Clean up the URL
        let finalUrl = urlToUse;

        // If it's a blob URL, skip it
        if (urlToUse.startsWith('blob:')) {
          console.log('Skipping blob URL:', urlToUse);
          return false;
        }

        // If it already starts with http/https, use it as is
        if (urlToUse.startsWith('http://') || urlToUse.startsWith('https://')) {
          finalUrl = urlToUse;
        }
        // If it starts with www, add https
        else if (urlToUse.startsWith('www.')) {
          finalUrl = 'https://' + urlToUse;
        }
        // If it looks like a domain (contains dots but no protocol), add https
        else if (
          urlToUse.includes('.') &&
          !urlToUse.includes('/') &&
          !urlToUse.startsWith('#')
        ) {
          finalUrl = 'https://' + urlToUse;
        }
        // If it's a fragment or relative path, skip it
        else if (
          urlToUse.startsWith('#') ||
          urlToUse.startsWith('/') ||
          !urlToUse.includes('.')
        ) {
          console.log('Skipping relative/fragment URL:', urlToUse);
          return false;
        }

        console.log('Opening PDF link in new tab:', finalUrl);
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
        return false;
      }

      // Also handle clicks on annotation elements that might not be proper <a> tags
      const annotationElement = event.target.closest(
        '.linkAnnotation, [data-annotation-type="Link"]'
      );
      if (annotationElement) {
        event.preventDefault();
        event.stopPropagation();

        // Try to extract URL from various annotation attributes
        const possibleUrl =
          annotationElement.getAttribute('data-url') ||
          annotationElement.getAttribute('title') ||
          annotationElement.textContent?.trim();

        if (possibleUrl) {
          // Check if this is a localhost URL
          if (
            possibleUrl.includes('localhost') ||
            possibleUrl.includes(window.location.host)
          ) {
            console.log('Skipping localhost annotation URL:', possibleUrl);
            return false;
          }

          // Only process if it looks like an external URL
          if (
            possibleUrl.includes('http://') ||
            possibleUrl.includes('https://') ||
            (possibleUrl.includes('www.') && possibleUrl.includes('.'))
          ) {
            let finalUrl = possibleUrl;
            if (
              !finalUrl.startsWith('http://') &&
              !finalUrl.startsWith('https://')
            ) {
              finalUrl = 'https://' + finalUrl.replace(/^www\./, 'www.');
            }
            console.log('Opening annotation link in new tab:', finalUrl);
            window.open(finalUrl, '_blank', 'noopener,noreferrer');
            return false;
          }
        }
      }
    },
    [originalAnnotations]
  );

  // Enhanced function to capture original annotation data
  const handleGetAnnotationsSuccess = useCallback((annotations, pageNumber) => {
    if (annotations && annotations.length > 0) {
      const pageAnnotationData = annotations
        .map((annotation) => ({
          id: annotation.id,
          url: annotation.url || annotation.unsafeUrl || annotation.dest,
          type: annotation.annotationType,
          rect: annotation.rect,
          // Store additional properties that might contain the original URL
          rawAnnotation: annotation
        }))
        .filter((ann) => ann.url);

      if (pageAnnotationData.length > 0) {
        setOriginalAnnotations((prev) => {
          const newMap = new Map(prev);
          newMap.set(pageNumber, pageAnnotationData);
          return newMap;
        });
        console.log(
          `Stored ${pageAnnotationData.length} annotations for page ${pageNumber}:`,
          pageAnnotationData
        );
      }
    }

    // Ensure all link annotations open in new tab
    annotations.forEach((annotation) => {
      if (annotation.annotationType === 2) {
        annotation.newWindow = true;
      }
    });
  }, []);

  if (loadError) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          color: '#f44336',
          backgroundColor: '#f5f5f5',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <div style={{ marginBottom: '16px' }}>{loadError}</div>
        <button
          onClick={handleManualRetry}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#2196f3',
            color: 'white',
            cursor: 'pointer'
          }}>
          Retry
        </button>
      </div>
    );
  }

  if (isZoomChanging) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          color: '#666',
          backgroundColor: '#f5f5f5',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <div>Adjusting to zoom level...</div>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #2196f3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginTop: '10px'
          }}></div>
      </div>
    );
  }

  return (
    <div className="materials-pdf-container" onClick={handleLinkClick}>
      <Document
        key={`document-${documentKey}`}
        ref={documentRef}
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        // onItemClick={onItemClick}
        externalLinkRel="noopener noreferrer"
        externalLinkTarget="_blank"
        loading={
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#666',
              fontSize: '16px'
            }}>
            Loading PDF...
          </div>
        }
        error={null}
        options={options}>
        {numPages &&
          Array.from(new Array(numPages), (el, index) => (
            <div
              key={`page_${index + 1}_${documentKey}`}
              className="materials-pdf-page-wrapper">
              <Page
                pageNumber={index + 1}
                data-page-number={index + 1}
                onRenderError={(error) => {
                  console.error(`Error rendering page ${index + 1}:`, error);
                }}
                loading={
                  <div
                    style={{
                      width: '100%',
                      height: '400px',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      color: '#666'
                    }}>
                    Loading page {index + 1}...
                  </div>
                }
                renderAnnotationLayer={true}
                renderTextLayer={true}
                // onGetAnnotationsSuccess={(annotations) =>
                //   handleGetAnnotationsSuccess(annotations, index + 1)
                // }
              />
            </div>
          ))}
      </Document>
    </div>
  );
};

const MaterialsPanelComponent = (props) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(false);

  const { path, info, call, setAudioPaths, booking } = props;

  const snackbar = useSnackbar();
  const auth = useContext(AuthContext);
  const classes = useStyles();
  const [width] = useWindowSize();

  const getUrl = async (path) => {
    try {
      const docUrl = await auth.api.materials.getLessonDocument(
        snackbar.enqueueSnackbar,
        path
      );
      setUrl(docUrl);
    } catch (error) {
      setError(error.message || 'Failed to fetch document URL');
    }
  };

  useEffect(() => {
    console.log('Retrying material fetch:', retry);
    async function getMaterialUrl() {
      try {
        setUrl(null);

        console.log('Fetching materials for topic:', path, info.topic);

        if (info && info.topic) {
          console.log('Fetching materials for topic:', info.topic);
          const allCourses = await auth.api.materials.getCourses(
            info.topic.material
          );

          const course = allCourses.find(
            (entry) => entry.id === info.topic.course
          );
          if (!course) throw new Error('Course not found');

          const lesson = course.lessons.find(
            (lesson) => lesson.id === info.topic.lesson
          );
          console.log({ foundLesson: lesson?.audioResource });
          if (!lesson) throw new Error('Lesson not found');
          if (lesson?.audioResource) {
            console.log('Setting audio paths:', lesson.audioResource, lesson);
            setAudioPaths(lesson?.audioResource);
          } else {
            console.log('No audio resources found, clearing audio paths');
            setAudioPaths(null);
          }

          await getUrl(lesson[`tutorResource`]);
        } else if (path) {
          await getUrl(path);
        } else {
          console.log('No path or info provided, skipping material fetch');
        }
      } catch (error) {
        setError(error.message || 'Error fetching materials');
      }
    }

    getMaterialUrl();
  }, [retry, booking?.lastUpdated, path, auth?.role]);

  const retryAPICall = useCallback(() => {
    setError(null);
    setRetry((prev) => !prev);
  }, []);

  if (error) {
    return (
      <div className={classes.status}>
        <AsyncStatus error={error} onRetry={retryAPICall} retry />
      </div>
    );
  }

  // CSS-only responsive container without JS calculations
  const containerStyles = {
    width: '100%',
    height: width < 1000 ? '60vh' : '100%',
    minHeight: '60vh',
    position: 'relative'
  };

  return (
    <div className={classes.root}>
      {url ? (
        <div style={containerStyles}>
          <PDFErrorBoundary
            onAutoRetry={() => setRetry((prev) => !prev)}
            onManualRetry={() => setRetry((prev) => !prev)}>
            <EnhancedPdfViewer url={url} />
          </PDFErrorBoundary>
        </div>
      ) : (
        <div style={containerStyles}>
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}>
            <Skeleton
              variant="rect"
              width="90%"
              height="80%"
              style={{ borderRadius: '4px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
const MemoizedMaterialsPanelComponent = memo(MaterialsPanelComponent);

MemoizedMaterialsPanelComponent.displayName = 'MaterialsPanel';
export const MaterialsPanel = MemoizedMaterialsPanelComponent;
